import { store, uid } from '@/firebase'
import {
  CollectionName,
  FSBookingPage,
  FSBookingPageMetaData,
  FSBookingPagePrivateParams,
  FSForm,
  FSMemberShare,
  FSUserPublicProfile,
  LocationParam,
  PrivatePageParams,
  PublicPageParams,
  WithId,
  generateDocId
} from '@WombatTechnology/nitte-time-lib'
import { compact, flatten, isNull, uniq } from 'es-toolkit/compat'
import {
  CollectionReference,
  DocumentData,
  DocumentReference,
  Timestamp,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  setDoc
} from 'firebase/firestore'
import { FormulaUser } from '../formulaUser'
import { UserPublicProfile } from '../userPublicProfile'
import { BookingPageMeataData } from './bookingPageMetaData'
import { MemberShare } from './bookingPages/memberShare'

// PublicとPrivateなパラメータを合わせてBookingPageとしたもの
export class BookingPage {
  static PAGE_ID_LENGTH = 8

  constructor(
    readonly uid: string,
    readonly id: string,
    readonly params: FSBookingPage<Timestamp, DocumentReference<FSForm>>,
    readonly privateParams: FSBookingPagePrivateParams,
    readonly metaData: FSBookingPageMetaData<Timestamp>,
    readonly ownerProfile: WithId<FSUserPublicProfile>,
    readonly sharedMemberUids: string[]
  ) {}

  get title(): string {
    return this.privateParams.title
  }

  get lengthMin(): number {
    return this.params.lengthMin
  }

  get locParam(): LocationParam {
    return this.params.location
  }

  get formula(): string[][] {
    return JSON.parse(this.params.formula) as string[][]
  }

  get flattenFormula(): string[] {
    return uniq(flatten(this.formula))
  }

  get url(): string {
    return BookingPage.toURL(this.uid, this.id)
  }

  get buffer(): number | null {
    return this.params.buffer
  }
  get room(): string[] | null {
    return this.params.room
  }

  get allowCancel(): boolean {
    return this.privateParams.allowCancel
  }

  get allowReschedule(): boolean {
    return this.privateParams.allowReschedule
  }

  get bookingCalendarId(): string | null {
    return this.privateParams.bookingCalendarId
  }

  get dateTimeRange() {
    return this.params.dateTimeRange
  }

  get daysLaterCanBeBooked() {
    return this.params.daysLaterCanBeBooked
  }

  get isShared() {
    return this.sharedMemberUids.length > 0
  }

  get isMyPage() {
    return this.uid === uid()!
  }

  get path(): string {
    return `/${this.uid}/${this.id}`
  }

  static doc(uid: string, pageId: string) {
    return doc<
      FSBookingPage<Timestamp, DocumentReference<FSForm>>,
      DocumentData
    >(this.collection(uid), pageId)
  }

  static collection(uid: string) {
    return collection(
      store,
      `${CollectionName.Users}/${uid}/${CollectionName.BookingPages}`
    ) as CollectionReference<
      FSBookingPage<Timestamp, DocumentReference<FSForm>>
    >
  }

  static privateParamsDoc(uid: string, pageId: string) {
    return doc<FSBookingPagePrivateParams, DocumentData>(
      this.privateParamsCollection(uid),
      pageId
    )
  }

  static privateParamsCollection(uid: string) {
    return collection(
      store,
      `${CollectionName.Users}/${uid}/${CollectionName.BookingPagePrivateParams}`
    ) as CollectionReference<FSBookingPagePrivateParams>
  }

  static async getPage(
    ownerUid: string,
    pageId: string
  ): Promise<BookingPage | null> {
    // 共有先メンバー一覧（誰に共有しているか）は所有者本人のみ取得できる（rules上listできるのは所有者のみ）。
    // 被共有ページではこの一覧は不要なので空配列にする。
    const isMyPage = ownerUid === uid()
    const [publicDoc, privateDoc, metaDataDoc, ownerProfile, sharedMemberUids] =
      await Promise.all([
        getDoc(BookingPage.doc(ownerUid, pageId)),
        getDoc(BookingPage.privateParamsDoc(ownerUid, pageId)),
        getDoc(BookingPageMeataData.doc(ownerUid, pageId)),
        UserPublicProfile.findMayBeNull(ownerUid),
        isMyPage
          ? this.getSharedMemberUids(ownerUid, pageId)
          : Promise.resolve<string[]>([])
      ])
    if (
      !publicDoc.exists() ||
      !privateDoc.exists() ||
      !metaDataDoc.exists() ||
      isNull(ownerProfile)
    )
      return null
    return new BookingPage(
      ownerUid,
      pageId,
      publicDoc.data() as FSBookingPage<Timestamp, DocumentReference<FSForm>>,
      privateDoc.data() as FSBookingPagePrivateParams,
      metaDataDoc.data() as FSBookingPageMetaData<Timestamp>,
      ownerProfile,
      sharedMemberUids
    )
  }

  static async getSharedPages(
    myUid: string,
    teamId: string
  ): Promise<BookingPage[]> {
    // 自分宛て(uid==myUid)かつ現在チーム(teamId)で共有されたページを取得する
    const qs = await getDocs(MemberShare.collectionQuery(myUid, teamId))

    const pagesMayBeNull = await Promise.all(
      qs.docs.map((shareDoc) => this.getPageFromMemberShare(shareDoc.ref))
    )
    return compact(pagesMayBeNull)
  }

  private static async getPageFromMemberShare(
    doc: DocumentReference<FSMemberShare>
  ) {
    const pageDoc = doc.parent.parent
    const userDoc = pageDoc!.parent.parent
    // 自分が所有するページは initUserPages で取得済みなので除外する
    if (userDoc!.id === uid()) return null
    return this.getPage(userDoc!.id, pageDoc!.id)
  }

  // 所有者が誰に共有しているか（共有先メンバーのuid一覧）を取得する。所有者本人のみ呼べる。
  private static async getSharedMemberUids(
    ownerUid: string,
    pageId: string
  ): Promise<string[]> {
    const qs = await getDocs(MemberShare.collection(ownerUid, pageId))
    return qs.docs.map((d) => d.data().uid)
  }

  static async deletePage(uid: string, pageId: string) {
    return Promise.all([
      deleteDoc(BookingPage.doc(uid, pageId)),
      deleteDoc(BookingPage.privateParamsDoc(uid, pageId))
    ])
  }

  async syncToPreview() {
    return Promise.all([
      // BookingPage
      // NOTE: シェアされたページである場合も考慮して、this.uidではなくuid()!を使う
      setDoc(BookingPage.doc(uid()!, 'preview'), {
        ...this.params,
        updatedAt: Timestamp.fromDate(new Date())
      }),
      // プライベートなパラメータは分けて保存
      setDoc(
        BookingPage.privateParamsDoc(uid()!, 'preview'),
        this.privateParams
      )
    ])
  }

  async duplicate(uid: string) {
    return BookingPage.createPage(
      uid,
      {
        ...this.params,
        name: `${this.params.name}のコピー`,
        eventTitle: this.params.eventTitle
          ? `${this.params.eventTitle}のコピー`
          : null
      },
      {
        ...this.privateParams,
        title: `${this.privateParams.title}のコピー`
      }
    )
  }

  uniqFormulaUsers(allFormulaUsers: FormulaUser[]) {
    const flattenUserIds = uniq(flatten(this.formula))
    const formulaUsersMayBeNull = flattenUserIds.map((id) =>
      allFormulaUsers.find((user) => user.id === id)
    )
    return compact(formulaUsersMayBeNull)
  }

  isAttendee(uid: string) {
    return this.flattenFormula.includes(uid)
  }

  static async createPage(
    uid: string,
    publicParams: PublicPageParams<DocumentReference<FSForm>>,
    privateParams: PrivatePageParams
  ) {
    const pageId = generateDocId(this.PAGE_ID_LENGTH)
    await Promise.all([
      BookingPage.setPage(uid, pageId, publicParams, privateParams),
      // メタデータを作成
      BookingPageMeataData.create(uid, pageId)
    ])
    return pageId
  }

  static async setPage(
    uid: string,
    pageId: string,
    publicParams: PublicPageParams<DocumentReference<FSForm>>,
    privateParams: PrivatePageParams
  ) {
    return Promise.all([
      // BookingPage
      setDoc(this.doc(uid, pageId), {
        ...publicParams,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date())
      }),
      // プライベートなパラメータは分けて保存
      setDoc(this.privateParamsDoc(uid, pageId), privateParams)
    ])
  }

  async deletePage() {
    return Promise.all([
      deleteDoc(BookingPage.doc(this.uid, this.id)),
      deleteDoc(BookingPage.privateParamsDoc(this.uid, this.id)),
      BookingPageMeataData.delete(this.uid, this.id)
    ])
  }

  static toURL(uid: string, pageId: string): string {
    return `${window.location.origin}/${uid}/${pageId}`
  }
}
